import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function Jinja() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/jinja-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Jinja
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Adventure Capital of East Africa & Source of the Nile
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Jinja is Uganda's adventure capital and the gateway to the Source of the Nile. Situated at the northern shore
                of Lake Victoria, this vibrant town sits at the exact point where the world's longest river, the Nile, begins
                its legendary 6,650 km journey to the Mediterranean Sea. The presence of Africa's most powerful river has shaped
                this region into a global hotspot for adventure tourism.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Beyond its natural significance, Jinja blends colonial charm with modern amenities. The town is a cultural
                melting pot where you can explore local craft markets, experience authentic Ugandan hospitality, and witness
                one of nature's most powerful forces. Whether you're seeking adrenaline-pumping activities or peaceful cultural
                experiences, Jinja offers the perfect balance.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Location</h3>
                  <p className="text-gray-700">Lake Victoria Shore</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Distance from Kampala</h3>
                  <p className="text-gray-700">80 km (1.5 hours)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Primary Feature</h3>
                  <p className="text-gray-700">Source of the Nile</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Best For</h3>
                  <p className="text-gray-700">Adventure Activities</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/jinja-hero.jpg"
                alt="Jinja - Source of the Nile"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 80 km via well-maintained Kampala-Jinja Highway</p>
                  <p><strong>Travel Time:</strong> 1.5-2 hours by private vehicle</p>
                  <p><strong>By Bus:</strong> Regular daily buses and matatus (2-3 hours). Budget-friendly option with frequent departures.</p>
                  <p className="text-sm italic">Private transfers available through tour operators for comfort and convenience.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Jinja Airstrip:</strong> Charter flights available from Entebbe International Airport (30-minute flight).</p>
                  <p><strong>Best For:</strong> Luxury travelers or those with limited time combining Jinja with safari destinations.</p>
                  <p className="text-sm italic">Charter flights can be arranged through aviation operators. Cost-effective for groups.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-bicycle" className="w-6 h-6" />
                  Getting Around Jinja
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Boda Bodas:</strong> Motorcycle taxis for quick, affordable transport within town.</p>
                  <p><strong>Taxis:</strong> Shared and private taxis readily available.</p>
                  <p><strong>Bicycles:</strong> Rental bikes available for exploring town at leisure.</p>
                  <p className="text-sm italic">Many adventure centers provide free shuttle services from hotels.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Source of the Nile</h3>
                <p className="text-gray-700">
                  Visit the legendary spot where the Nile River begins its 6,650 km journey to the Mediterranean. Historical site where 19th-century explorers sought Africa's greatest mystery.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-swimming" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">World-Class White-Water Rafting</h3>
                <p className="text-gray-700">
                  Conquer Class III-V rapids on the mighty Nile. Rated among the top 10 rafting destinations globally, offering thrilling runs suitable for all experience levels.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-falling" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Nile Bungee Jumping</h3>
                <p className="text-gray-700">
                  Experience a heart-pounding 44-meter freefall over the Nile River. One of East Africa's most iconic bungee jumps with stunning river gorge views.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-sun" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Lake Victoria Cruises</h3>
                <p className="text-gray-700">
                  Scenic boat cruises on Africa's largest lake. Enjoy sunset views, fishing expeditions, and island hopping with opportunities for birdwatching.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-cart-shopping" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Local Craft Markets</h3>
                <p className="text-gray-700">
                  Explore vibrant markets featuring handmade crafts, artwork, and traditional artifacts. Perfect for authentic souvenirs and experiencing Ugandan artisan culture.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-building" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Colonial Architecture</h3>
                <p className="text-gray-700">
                  Discover Jinja's rich colonial history through preserved buildings, temples, and heritage sites reflecting its diverse cultural past.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">White-Water Rafting</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Full-day rafting adventure tackling Class III-V rapids. Professional guides, safety equipment provided. Half-day and multi-day options available. No prior experience needed.
                </p>
                <p className="text-safari-gold font-bold mb-3">From $110 per person</p>
                <Link to="/activities/whitewater-rafting" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-falling-burst" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Bungee Jumping</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  44-meter leap from a platform over the Nile. Operated by Adrift, with impeccable safety record. Photos and videos included. One of East Africa's most thrilling jumps.
                </p>
                <p className="text-safari-gold font-bold mb-3">From $75 per person</p>
                <Link to="/activities/bungee-jumping" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-rowing" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Kayaking Adventures</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Paddle calm stretches and navigate gentle rapids. Half-day to multi-day kayaking safaris available. Great for wildlife viewing and connecting with nature. Beginner-friendly options.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-8 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate to Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $80 per person</p>
                <Link to="/activities/kayaking-adventures" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-landmark" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Source of the Nile Tour</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Guided visit to Ripon Falls and the Source monuments. Learn fascinating history of Speke, Burton, and the great Nile quest. Includes boat ride. Suitable for all ages.
                </p>
                <Link to="/activities/source-of-the-nile" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-bicycle" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Quad Biking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Explore Jinja countryside on powerful quad bikes. Traverse village paths, sugarcane plantations, and scenic trails. 2-3 hour adventures with village stops and cultural interactions.
                </p>
                <Link to="/activities/quad-biking" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-sailboat" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Sunset Cruise</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Evening boat cruise on Lake Victoria with drinks and snacks. Experience magical sunset views over Africa's largest lake. Relaxing atmosphere perfect after active adventures.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $50 per person</p>
                <Link to="/activities/sunset-cruise" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-horse" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Horseback Riding</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Explore scenic Nile-side trails on horseback. Guided rides through countryside and riverbanks suitable for all skill levels. Experience peaceful wildlife encounters from horseback.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 1-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $60 per person</p>
                <Link to="/activities/horseback-riding-jinja" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-swimming" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Stand-Up Paddleboarding</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Glide across calm waters and mild rapids on stand-up paddleboards. Great core workout combined with beautiful river scenery. Instruction provided for beginners.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 1-2 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <Link to="/activities/stand-up-paddleboarding" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-biking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mountain Biking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Pedal through riverside trails and village paths on guided mountain bike tours. Explore Jinja's countryside, local communities, and scenic viewpoints on two wheels.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $45 per person</p>
                <Link to="/activities/mountain-biking-jinja" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Year-Round Destination:</strong> Adventure activities available all seasons. Water levels remain consistent for rafting.</p>
                  <p><strong>Dry Seasons (June-August, December-February):</strong> Peak tourist season with optimal weather conditions. Best for outdoor activities and photography.</p>
                  <p><strong>Wet Seasons (March-May, September-November):</strong> Higher water levels make rafting more exciting. Fewer crowds and potential discounts.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Swimsuit, quick-dry clothing, water shoes or sandals, waterproof bag for electronics</p>
                  <p><strong>Sun Protection:</strong> Sunscreen (high SPF), sunglasses, hat, light long-sleeved shirt</p>
                  <p><strong>Optional:</strong> GoPro or waterproof camera, change of clothes, insect repellent</p>
                  <p className="text-sm italic">Most adventure operators provide safety equipment. Bring cash for tipping guides.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Fitness Level:</strong> Most activities suitable for moderate fitness. Swimming ability required for rafting.</p>
                  <p><strong>Medical:</strong> Malaria prophylaxis recommended. Yellow fever certificate required for entry to Uganda.</p>
                  <p><strong>Safety Standards:</strong> All major operators maintain international safety certifications. Follow guide instructions carefully.</p>
                  <p><strong>Age Limits:</strong> Minimum age 15 for rafting and bungee (some operators 18). Family-friendly activities available.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Accommodation:</strong> Options from budget backpackers to luxury riverside lodges. Book in advance during peak season.</p>
                  <p><strong>Combo Packages:</strong> Many operators offer multi-activity packages at discounted rates.</p>
                  <p><strong>Food & Drink:</strong> Local restaurants affordable. Riverside dining more expensive but scenic.</p>
                  <p><strong>Transport:</strong> Boda bodas cheapest option. Negotiate fares before departure.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Jinja offers accommodation options ranging from budget backpacker hostels to luxury riverside lodges, many with Nile views.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Wildwaters Lodge</td>
                  <td className="px-6 py-4">Private island on Nile</td>
                  <td className="px-6 py-4">Luxury suites, infinity pool, spa, fine dining, exclusive</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Lemala Wildwaters Camp</td>
                  <td className="px-6 py-4">Nile riverfront</td>
                  <td className="px-6 py-4">Tented suites, panoramic views, gourmet meals, adventure center</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">The Haven Lodge</td>
                  <td className="px-6 py-4">Bujagali Falls</td>
                  <td className="px-6 py-4">River views, restaurant, bar, pool, activity booking</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">2 Friends Guesthouse</td>
                  <td className="px-6 py-4">Jinja town center</td>
                  <td className="px-6 py-4">Comfortable rooms, garden setting, excellent restaurant</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Nile River Explorers Campsite</td>
                  <td className="px-6 py-4">Bujagali</td>
                  <td className="px-6 py-4">Camping, dorms, private rooms, bar, social atmosphere</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Nile Porch</td>
                  <td className="px-6 py-4">Near Source of Nile</td>
                  <td className="px-6 py-4">Basic bandas, riverside location, backpacker friendly</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Do I need rafting experience?</h3>
                <p className="text-gray-700">
                  No prior experience necessary. Professional guides provide safety briefings and instruction. Choose between Grade 3 (moderate) or Grade 5 (extreme) rapids based on comfort level.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is rafting safe?</h3>
                <p className="text-gray-700">
                  Yes, when done with reputable operators. All major companies follow international safety standards with certified guides, quality equipment, and safety kayakers. Incidents are rare.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long does rafting take?</h3>
                <p className="text-gray-700">
                  Full-day rafting (6-8 hours total): 4-5 hours on water covering 20+ rapids, plus transport, briefing, and lunch. Half-day options (3-4 hours total) also available.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the best time for rafting?</h3>
                <p className="text-gray-700">
                  Year-round activity with consistent water levels. September-November offers higher water for more excitement. June-August provides optimal weather conditions.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can non-swimmers do rafting?</h3>
                <p className="text-gray-700">
                  Swimming ability highly recommended for safety. Life jackets mandatory for all participants. Non-swimmers should consider gentler activities like kayaking or boat cruises.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long should I stay in Jinja?</h3>
                <p className="text-gray-700">
                  Minimum 2-3 days recommended to experience major activities without rushing. Adventure enthusiasts often spend 4-5 days combining multiple activities and relaxation.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Jinja with safari destinations?</h3>
                <p className="text-gray-700">
                  Absolutely! Jinja pairs perfectly with Murchison Falls (5 hours north) or eastern Uganda destinations. Many itineraries combine adventure and wildlife experiences.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What about photos and videos?</h3>
                <p className="text-gray-700">
                  Professional photographers capture action shots during rafting and bungee jumping. Photos/videos available for purchase. Bring waterproof camera or GoPro for personal footage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience the Source of the Nile
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Jinja awaits with world-class adventure activities, breathtaking natural beauty, and rich cultural experiences.
            Whether you're an adrenaline junkie or a cultural explorer, the Nile's source promises unforgettable memories.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Jinja Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
